import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { sendRconCommand, fetchRconPresets, type RconPreset } from '../../api/rcon';
import { PresetManager } from './PresetManager';
import type { GameItem } from '../../api/games';

interface HistoryEntry {
  command: string;
  output: string;
  error?: string;
}

interface Props {
  game: GameItem | null;
  onClose: () => void;
}

export function RconConsole({ game, onClose }: Props) {
  const open = game !== null && (game.rcon?.enabled ?? false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [presets, setPresets] = useState<RconPreset[]>([]);
  const [presetManagerOpen, setPresetManagerOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Load presets on open
  useEffect(() => {
    if (!open || !game) return;
    setHistory([]);
    setCommandHistory([]);
    setHistoryIdx(-1);
    setInput('');
    fetchRconPresets(game.id)
      .then((r) => setPresets(r.presets))
      .catch(() => setPresets([]));
  }, [open, game?.id]);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const send = async (command: string) => {
    if (!command.trim() || !game || sending) return;
    setSending(true);
    const cmd = command.trim();
    try {
      const { output } = await sendRconCommand(game.id, cmd);
      setHistory((prev) => [...prev, { command: cmd, output }]);
      setCommandHistory((prev) => {
        const next = [cmd, ...prev.filter((c) => c !== cmd)].slice(0, 50);
        return next;
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'RCON error';
      setHistory((prev) => [...prev, { command: cmd, output: '', error: msg }]);
    } finally {
      setSending(false);
      setHistoryIdx(-1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void send(input);
      setInput('');
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(historyIdx + 1, commandHistory.length - 1);
      setHistoryIdx(next);
      if (commandHistory[next] !== undefined) setInput(commandHistory[next]);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.max(historyIdx - 1, -1);
      setHistoryIdx(next);
      setInput(next === -1 ? '' : (commandHistory[next] ?? ''));
    }
  };

  if (!game) return null;

  if (!game.rcon?.enabled) {
    return (
      <Dialog open={game !== null} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center">
            <Typography variant="h6" sx={{ flexGrow: 1 }}>RCON Console — {game.id}</Typography>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">RCON is not enabled for this game. Enable it in the game's settings.</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>RCON Console — {game.name}</Typography>
            <Tooltip title="Manage presets">
              <IconButton size="small" onClick={() => setPresetManagerOpen(true)}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Preset chips */}
          {presets.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {presets.map((p, i) => (
                <Chip
                  key={i}
                  label={p.label}
                  size="small"
                  variant="outlined"
                  onClick={() => void send(p.command)}
                  disabled={sending}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          )}

          {/* Output area */}
          <Box
            sx={{
              flex: 1,
              minHeight: 320,
              maxHeight: 400,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              background: '#141414',
              color: '#e0e0e0',
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {history.length === 0 && (
              <Typography variant="caption" color="text.disabled">
                Type a command and press Enter.
              </Typography>
            )}
            {history.map((h, i) => (
              <Box key={i} sx={{ mb: 0.75 }}>
                <Box sx={{ color: '#888' }}>&gt; {h.command}</Box>
                {h.error ? (
                  <Box sx={{ color: '#f87171' }}>{h.error}</Box>
                ) : (
                  <Box sx={{ whiteSpace: 'pre-wrap', color: '#c5c5c5' }}>
                    {h.output || <span style={{ color: '#555', fontStyle: 'italic' }}>{'(no output)'}</span>}
                  </Box>
                )}
              </Box>
            ))}
            <div ref={bottomRef} />
          </Box>

          {/* Input */}
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter command…"
            size="small"
            fullWidth
            disabled={sending}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.85rem' }}>
                    &gt;
                  </Typography>
                </InputAdornment>
              ),
              endAdornment: sending ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.disabled">
              Enter to send · ↑/↓ to recall history
            </Typography>
            <Button
              size="small"
              variant="contained"
              disabled={!input.trim() || sending}
              onClick={() => { void send(input); setInput(''); }}
            >
              Send
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <PresetManager
        open={presetManagerOpen}
        gameId={game.id}
        initialPresets={presets}
        onClose={(saved) => {
          setPresets(saved);
          setPresetManagerOpen(false);
        }}
      />
    </>
  );
}
